import { Box, Button, HStack, Link, Text, VStack } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { CreditFacilitySetupPageProps } from "features/credit-facility/setup/CreditFacilitySetupPageProps";
import {
  getCreditFacilityBorrowingBaseGroupListQueryKey,
  useCreditFacilityBorrowingBaseGroupDelete,
  useCreditFacilityBorrowingBaseGroupsUpsert,
  useCreditFacilityBorrowingBaseGroupUpsert,
} from "gen/endpoints/credit-facility-borrowing-base-group/credit-facility-borrowing-base-group";
import { getCreditFacilityBorrowingBaseSummaryTableRetrieveQueryKey } from "gen/endpoints/credit-facility-borrowing-base-group-summary-table/credit-facility-borrowing-base-group-summary-table";
import { getCreditFacilityBorrowingBaseInvestorEntitiesTableDataRetrieveQueryKey } from "gen/endpoints/credit-facility-borrowing-base-investors/credit-facility-borrowing-base-investors";
import { CreditFacilityBorrowingBaseGroupCreateAPIDataRequest } from "gen/model/creditFacilityBorrowingBaseGroupCreateAPIDataRequest";
import { CreditFacilityBorrowingBaseGroupReadData } from "gen/model/creditFacilityBorrowingBaseGroupReadData";
import { CreditFacilityBorrowingBaseGroupWithCountData } from "gen/model/creditFacilityBorrowingBaseGroupWithCountData";
import { useEffect } from "react";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Form, { FormHandle } from "shared/components/form/Form";
import { FormFieldset } from "shared/components/form/FormFieldset";
import {
  FormNumberInput,
  NumberInput,
} from "shared/components/input/text/NumberInput";
import TextInput, {
  FormTextInput,
} from "shared/components/input/text/TextInput";
import { usePageWizard } from "shared/components/layout/PageWizard";
import { rootRoute } from "shared/navigation/Routes";
import { setFormValueIfChanged } from "shared/utils/FormUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

interface BorrowingBaseGroupListFormProps {
  hasConcentrationLimit: boolean;
  shouldSelectInvestors?: () => Promise<boolean>;
  formRef: React.RefObject<FormHandle>;
  borrowingBaseGroupData: CreditFacilityBorrowingBaseGroupCreateAPIDataRequest;
}

export const BorrowingBaseGroupListForm = ({
  borrowingBaseGroupData,
  hasConcentrationLimit,
  shouldSelectInvestors,
  formRef,
}: BorrowingBaseGroupListFormProps) => {
  const { t } = useTranslation(["creditFacility"]);
  const { fundFamilyId, creditFacilityId, creditFacility } =
    useOutletContext<CreditFacilitySetupPageProps>();
  const queryClient = useQueryClient();
  const { navigateNext } = usePageWizard();
  const { mutateAsync: deleteGroup } =
    useCreditFacilityBorrowingBaseGroupDelete();

  const form = useForm<CreditFacilityBorrowingBaseGroupCreateAPIDataRequest>({
    values: borrowingBaseGroupData,
  });

  const { mutateAsync: updateBorrowingGroups } =
    useCreditFacilityBorrowingBaseGroupsUpsert();

  const {
    fields: borrowingGroups,
    append: addBase,
    remove: removeBase,
  } = useFieldArray({
    name: "borrowing_base_groups",
    control: form.control,
    keyName: "fieldId",
  });
  const submitGroups = async (
    data: CreditFacilityBorrowingBaseGroupCreateAPIDataRequest,
  ): Promise<CreditFacilityBorrowingBaseGroupReadData[]> => {
    const newData = await updateBorrowingGroups({
      fundFamilyId,
      creditFacilityId,
      data,
      params: {
        version_date: creditFacility.inception_date,
      },
    });
    await invalidateQueryKeys(
      queryClient,
      getCreditFacilityBorrowingBaseGroupListQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
      getCreditFacilityBorrowingBaseSummaryTableRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
    );
    return newData;
  };

  return (
    <Form
      disableNavBlocker={true}
      form={form}
      onSubmit={async (
        data: CreditFacilityBorrowingBaseGroupCreateAPIDataRequest,
      ) => {
        const isDefaultOnly =
          data.borrowing_base_groups.length === 1 &&
          data.borrowing_base_groups[0].id === "" &&
          data.borrowing_base_groups[0].name === "";
        if (!isDefaultOnly) {
          await submitGroups(data);
        }
      }}
      onSuccess={navigateNext}
      formRef={formRef}
    >
      <ContentBlockGroup allowToggle={false}>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              {t("creditFacility:borrowingBase.setUpBorrowingBase")}
            </ContentBlockTitle>
          </ContentBlockHeader>
          <ContentBlockBody>
            <Box pr="40px">
              {borrowingGroups.map((group, index) => (
                <BorrowingBaseGroupFields
                  borrowingBaseGroupId={group.id}
                  key={index}
                  hasConcentrationLimit={hasConcentrationLimit}
                  index={index}
                  investorCount={
                    (
                      group as unknown as CreditFacilityBorrowingBaseGroupWithCountData
                    ).investor_entity_count || 0
                  }
                  onClickDelete={async (groupId) => {
                    if (groupId) {
                      await deleteGroup({
                        fundFamilyId,
                        creditFacilityId,
                        params: { borrowing_base_group_id: groupId },
                      });
                      await queryClient.invalidateQueries({
                        queryKey:
                          getCreditFacilityBorrowingBaseGroupListQueryKey(
                            fundFamilyId,
                            creditFacilityId,
                          ),
                      });
                      await queryClient.invalidateQueries({
                        queryKey:
                          getCreditFacilityBorrowingBaseSummaryTableRetrieveQueryKey(
                            fundFamilyId,
                            creditFacilityId,
                          ),
                      });
                    } else {
                      removeBase(index);
                    }
                  }}
                  shouldSelectInvestors={shouldSelectInvestors}
                />
              ))}
              <ExcludedInvestors />
              <Button
                colorScheme="secondary"
                size="sm"
                onClick={() =>
                  addBase({
                    fund_family_id: fundFamilyId,
                    credit_facility_id: creditFacilityId,
                  } as never)
                }
              >
                {t("creditFacility:borrowingBase.update.addGroup")}
              </Button>
            </Box>
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
    </Form>
  );
};

interface BorrowingBaseGroupFieldsProps {
  investorCount: number;
  index: number;
  shouldSelectInvestors?: () => Promise<boolean>;
  onClickDelete: (groupId: string | undefined | null) => Promise<void>;
  borrowingBaseGroupId: string | undefined | null;
  hasConcentrationLimit: boolean;
}

const BorrowingBaseGroupFields: React.FC<BorrowingBaseGroupFieldsProps> = ({
  onClickDelete,
  investorCount,
  shouldSelectInvestors,
  index,
  borrowingBaseGroupId,
  hasConcentrationLimit,
}) => {
  const { t } = useTranslation(["creditFacility"]);
  const navigate = useNavigate();
  const { fundFamilyId, creditFacility, creditFacilityId } =
    useOutletContext<CreditFacilitySetupPageProps>();
  const creditFacilityRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .creditFacilities({})
    .creditFacility({ creditFacilityId });
  const queryClient = useQueryClient();
  const { mutateAsync: submitGroup } =
    useCreditFacilityBorrowingBaseGroupUpsert();

  const form =
    useFormContext<CreditFacilityBorrowingBaseGroupCreateAPIDataRequest>();
  const inputs = form.watch(`borrowing_base_groups.${index}`);

  const isDisabled = inputs.name === "" || inputs.rate === "";

  const goToInvestorPage = async () => {
    const isValid = await form.trigger();
    if (shouldSelectInvestors) {
      const canContinue = await shouldSelectInvestors();
      if (!canContinue) {
        return;
      }
    }
    if (isValid) {
      const data = await submitGroup({
        fundFamilyId,
        creditFacilityId,
        data: {
          ...inputs,
          version_date: creditFacility.inception_date,
        },
      });
      await queryClient.invalidateQueries({
        queryKey:
          getCreditFacilityBorrowingBaseInvestorEntitiesTableDataRetrieveQueryKey(
            fundFamilyId,
            creditFacilityId,
            data.id,
          ),
      });
      await queryClient.invalidateQueries({
        queryKey: getCreditFacilityBorrowingBaseSummaryTableRetrieveQueryKey(
          fundFamilyId,
          creditFacilityId,
        ),
      });
      navigate(
        creditFacilityRoute.setupInvestorBorrowingBase({
          borrowingBaseGroupId: data.id,
        }).$,
      );
    }
  };

  return (
    <Box width="600px">
      <FormFieldset>
        <VStack alignItems="flex-start" mb="24px">
          <Text textStyle="overline">
            {t("creditFacility:borrowingBase.update.settings")}
          </Text>
          <FormTextInput
            showMessages={true}
            name={`borrowing_base_groups.${index}.name`}
            title={t(
              "creditFacility:borrowingBase.update.advanceRateGroupName",
            )}
          />
          <FormNumberInput
            showMessages={true}
            name={`borrowing_base_groups.${index}.rate`}
            valueType="percent"
            title={t(
              "creditFacility:borrowingBase.update.unfundedCommitmentAvailableForBorrowing",
            )}
          />
          <ConcentrationLimitInputs
            hasConcentrationLimit={hasConcentrationLimit}
            index={index}
          />
          {investorCount ? (
            <HStack>
              <Text fontSize="18px">{`${investorCount} ${t(
                "creditFacility:borrowingBase.update.investorEntities",
              )}`}</Text>
              <Link
                size="sm"
                colorScheme="primary"
                onClick={() => !isDisabled && goToInvestorPage()}
              >
                {t("creditFacility:borrowingBase.update.edit")}
              </Link>
            </HStack>
          ) : (
            <>
              <Text textStyle="overline">
                {t("creditFacility:borrowingBase.update.addInvestors")}
              </Text>
              <Button
                width="100%"
                colorScheme="secondary"
                isDisabled={isDisabled}
                onClick={goToInvestorPage}
              >
                {t("creditFacility:borrowingBase.update.selectInvestors")}
              </Button>
            </>
          )}
        </VStack>
        <HStack justifyContent="flex-end">
          <Link
            size="sm"
            colorScheme="gray"
            onClick={async () => {
              await onClickDelete(borrowingBaseGroupId);
            }}
          >
            {t("creditFacility:borrowingBase.update.deleteAdvanceRateGroup")}
          </Link>
        </HStack>
      </FormFieldset>
    </Box>
  );
};

const ConcentrationLimitInputs: React.FC<{
  hasConcentrationLimit: boolean;
  index: number;
}> = ({ hasConcentrationLimit, index }) => {
  const { t } = useTranslation(["creditFacility"]);
  const individualLimitName =
    `borrowing_base_groups.${index}.individual_concentration_limit` as "borrowing_base_groups.0.individual_concentration_limit";
  const aggregateLimitName =
    `borrowing_base_groups.${index}.aggregate_concentration_limit` as "borrowing_base_groups.0.aggregate_concentration_limit";
  const form =
    useFormContext<CreditFacilityBorrowingBaseGroupCreateAPIDataRequest>();
  useEffect(() => {
    if (!hasConcentrationLimit) {
      setFormValueIfChanged(form, individualLimitName, null);
      setFormValueIfChanged(form, aggregateLimitName, null);
    }
  }, [form, hasConcentrationLimit, individualLimitName, aggregateLimitName]);
  return hasConcentrationLimit ? (
    <>
      <FormNumberInput
        showMessages={true}
        name={individualLimitName}
        unsetWhenEmpty={true}
        valueType="percent"
        title={t(
          "creditFacility:borrowingBase.concentrationLimits.individualConcentrationLimit",
        )}
        helpText={t(
          "creditFacility:borrowingBase.concentrationLimits.individualConcentrationLimitHelpText",
        )}
      />
      <FormNumberInput
        showMessages={true}
        helpText={t(
          "creditFacility:borrowingBase.concentrationLimits.aggregateConcentrationLimitHelpText",
        )}
        name={aggregateLimitName}
        valueType="percent"
        unsetWhenEmpty={true}
        title={t(
          "creditFacility:borrowingBase.concentrationLimits.aggregateConcentrationLimit",
        )}
      />
    </>
  ) : null;
};

const ExcludedInvestors = () => {
  const { t } = useTranslation(["creditFacility"]);
  return (
    <Box width="600px">
      <FormFieldset>
        <VStack alignItems="flex-start" mb="24px">
          <Text mt="10px" textStyle="overline">
            {t("creditFacility:borrowingBase.update.excludedInvestors")}
          </Text>
          <Box opacity="0.4" mb="16px">
            <Text mb="10px">
              {t("creditFacility:borrowingBase.update.excludedInvestorsInfo")}
            </Text>
            <Text>
              {t(
                "creditFacility:borrowingBase.update.excludedInvestorsRateInfo",
              )}
            </Text>
          </Box>
          <Text textStyle="overline">
            {t("creditFacility:borrowingBase.update.settings")}
          </Text>
          <TextInput
            isDisabled={true}
            title={t(
              "creditFacility:borrowingBase.update.advanceRateGroupName",
            )}
            value={t("creditFacility:borrowingBase.update.excludedInvestors")}
          />
          <NumberInput
            valueType="percent"
            isDisabled={true}
            title={t(
              "creditFacility:borrowingBase.update.unfundedCommitmentAvailableForBorrowing",
            )}
            value="0"
          />
        </VStack>
      </FormFieldset>
    </Box>
  );
};
